import axios from "axios";
import React, { createRef, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/file";
import { connect } from "react-redux";
import { 
    withRouter,
    useParams 
} from "react-router-dom";
import { withNamespaces } from "react-i18next";
import {
    Alert,
    Card,
    CardBody,
    CardHeader,
    CardText,
    Col,
    Container,
    ListGroup,
    ListGroupItem,
    Row,
} from "reactstrap";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
    API_URL_GET_E_LEARNING_DETAILS,
    API_URL_GET_E_LEARNING_AGENDA,
    API_URL_GET_E_LEARNING_COUNTER,
} from "../../common/constants";
import "../ReactPlayer.css";
import Player from "./Player";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

const DESIRED_TIME = 5 * 60; // 5min

const ElearningDetail = ({
    t,
    token,
    history
}) => {
    const params = useParams();
    const videoPlayerRef = useRef();
    const [ playerCountCalled, setPlayerCounCalled ] = useState(false)

    const {
        data: elearningDetails,
        isFetching: fetchingElearningDetails
    } = useQuery({
        queryKey: [
            "fetch-e-learning-details-query",
            params.id
        ],
        queryFn: async () => {
            const response = await axios.post(
                API_URL_GET_E_LEARNING_DETAILS,
                { 
                   lesson_id: Number(params.id),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data?.message || null;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching the E-Learning details."), {
                type: "error"
            });

            history.goBack();
        }
    });

    const {
        data: chapters,
        isFetching: loadingChapters
    } = useQuery({
        queryKey: [
            "fetch-e-learning-chapters-query",
            params.id
        ],
        queryFn: async () => {
            const response = await axios.post(
                API_URL_GET_E_LEARNING_AGENDA,
                { 
                   lesson_id: Number(params.id),
                   endpoint_id: 2
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data?.data || null;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: elearningDetails?.elearning_type === 'old',
        onError: () => {
            toast(t("An error occurred while fetching the E-Learning details."), {
                type: "error"
            });
        }
    });

    useEffect(() => {
        if(elearningDetails){
            if(elearningDetails.elearning_type === 'new'){
                localStorage.setItem(
                    elearningDetails.scorm_token_key, 
                    elearningDetails.scorm_token_value
                )
            }
        }
    }, [
        elearningDetails
    ]);

    const convertToSeconds = (str) => {
        const [minutes, seconds] = str.split(":");
        return Number(minutes) * 60 + Number(seconds);
    }

    const handleReactPlayerOnProgress = ({
        playedSeconds
    }) => {
        if(playedSeconds > DESIRED_TIME && !playerCountCalled){
            axios.get(API_URL_GET_E_LEARNING_COUNTER, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then(() => [
                setPlayerCounCalled(true)
            ])
        }
    }

    const renderContent = () => {
        if(fetchingElearningDetails || !elearningDetails){
            return (
                <Row>
                    <Col className="col-12">
                        <Alert
                            color="info"
                        >
                            {t('Loading')}...
                        </Alert>
                    </Col>
                </Row>
            )
        }

        if(elearningDetails?.elearning_type === 'new'){
            return (
                <Row>
                    <Col className="col-12">
                        <Player
                            data={elearningDetails}
                        />
                    </Col>
                </Row>
            )
        }

        return (
            <Row>
                <Col sm='12' md='8' lg='9'>
                    <Card>
                        <CardBody>
                            <div className="mb-2">
                                <CardHeader tag={"h1"}>
                                    {elearningDetails.lesson_name}
                                </CardHeader>
                            </div>

                            <div>
                                <div className="mb-2">
                                    <ReactPlayer
                                        controls
                                        className="react-player"
                                        width="100%"
                                        height="50%"
                                        playsinline
                                        url={elearningDetails.video_address}
                                        ref={videoPlayerRef}
                                        onProgress={handleReactPlayerOnProgress}
                                    />
                                </div>
                                <div>
                                    {elearningDetails.lesson_description ? (
                                        <CardText>
                                            <span>
                                                {t("Description")}:
                                            </span>
                                            {elearningDetails.lesson_description}
                                        </CardText>
                                    ) : null}
                                    {elearningDetails.lesson_language_id ? (
                                        <CardText>
                                            <span>{t("Language")}: </span>
                                            {elearningDetails.lesson_language_id}
                                        </CardText>
                                    ) : null}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm='12' md='4' lg='3'>
                    <Card>
                        <CardBody>
                            <div className="mb-2">
                                <CardHeader tag={"h3"}>
                                    {t("Chapters")}
                                </CardHeader>
                            </div>
                            <ListGroup>
                                {loadingChapters ? (
                                    <>{t('Loading')}...</>
                                ) : (
                                    <>
                                        {chapters.length ? (
                                            <>
                                                {chapters.map((chapter, chapterIndex) => {
                                                    return (
                                                        <ListGroupItem
                                                            action
                                                            tag="button"
                                                            key={chapterIndex}
                                                            onClick={() => {
                                                                videoPlayerRef.current.seekTo(
                                                                    convertToSeconds(chapter.time),
                                                                    "seconds"
                                                                );
                                                            }}
                                                        >
                                                            {chapter.agenda_name}
                                                        </ListGroupItem>
                                                    )
                                                })}
                                            </>
                                        ) : (
                                            <Alert color="warning">
                                                {t('No Agenda Available')}
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        )
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs
                    title={t("E-Learning")}
                    breadcrumbItems={[
                        { 
                            title: "SIP", 
                            link: "/"
                        },
                        { 
                            title: t("E-Learning"), 
                            link: "#" 
                        }
                    ]}
                />

                {renderContent()}
            </Container>
        </div>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return { token };
};

export default withNamespaces()(
    withRouter(
        connect(mapStatetoProps)(
            ElearningDetail
        )
    )
);
